/**
 * El eje de tiempo, compartido por las tres vistas.
 *
 * El Gantt tenia tres escalas fijas —dia, semana, mes— en tres botones. Es la forma mas comun
 * y la peor: cambiar de escala **salta**, se pierde donde estabas mirando, y ninguna de las
 * tres suele ser la que hace falta. Un plan de cinco semanas no entra en "dia" y se ve como una
 * mancha en "mes".
 *
 * Aca la escala es continua —pixeles por dia— y el zoom **ancla en el puntero**: lo que estabas
 * mirando se queda donde esta. La cabecera decide sola cuanto detalle mostrar segun el espacio
 * que hay, en vez de que lo elija una persona.
 *
 * Es geometria pura y sin React a proposito: la usan el plan, la franja de carga y la de
 * conflictos, y **tienen que dar el mismo numero**. Dos calculos de la misma columna se
 * desincronizan por un pixel y las tres vistas dejan de estar alineadas, que es justo lo unico
 * que esta pantalla tiene que garantizar.
 */

export const PX_POR_DIA_MIN = 3;
export const PX_POR_DIA_MAX = 64;
export const PX_POR_DIA_DEFECTO = 14;

export const ALTO_DE_FILA = 28;
export const ALTO_DE_PERSONA = 30;

const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const DOW = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

export function aFecha(iso) {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function aIso(d) {
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null;
}

export function sumarDias(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function diasEntre(a, b) {
  return Math.round((b - a) / 86400000);
}

/** ISO: 1 = lunes … 7 = domingo, igual que `services/horario_laboral.py`. */
function isoDelDia(d) {
  return d.getDay() === 0 ? 7 : d.getDay();
}

/**
 * Si el area **no** trabaja ese dia, con el calendario que declara la capsula.
 *
 * No es "sabado o domingo": una capsula de taller puede trabajar los sabados, y un feriado no
 * es un dia laborable aunque caiga en martes. Sin esto la pantalla sombrea dos dias que la
 * capsula si trabaja y cuenta como habiles dos que no, y el largo que dibuja al arrastrar no
 * coincide con el que devuelve el backend.
 */
export function esNoLaborable(d, horario) {
  if (!d) return false;
  const dias = horario?.dias || [1, 2, 3, 4, 5];
  if (!dias.includes(isoDelDia(d))) return true;
  return (horario?.feriados || []).includes(aIso(d));
}

/** Compatibilidad: fin de semana calendario, sin mirar la capsula. */
export function esFinde(d) {
  const n = d.getDay();
  return n === 0 || n === 6;
}

/** Cuantos dias de trabajo hay entre dos fechas, contando las dos puntas. */
export function diasHabilesEntre(desde, hasta, horario) {
  if (!desde || !hasta || hasta < desde) return 0;
  let n = 0;
  for (let d = new Date(desde); d <= hasta; d = sumarDias(d, 1)) {
    if (!esNoLaborable(d, horario)) n += 1;
  }
  return n;
}

/** La fecha del dia de trabajo numero `n` contando el arranque como el primero. */
export function sumarDiasHabiles(inicio, n, horario) {
  let d = new Date(inicio);
  while (esNoLaborable(d, horario)) d = sumarDias(d, 1);
  let faltan = Math.max(1, n) - 1;
  while (faltan > 0) {
    d = sumarDias(d, 1);
    while (esNoLaborable(d, horario)) d = sumarDias(d, 1);
    faltan -= 1;
  }
  return d;
}

/** El proximo dia que el area trabaja, o el mismo si ya lo es. */
export function proximoHabil(d, horario) {
  let x = new Date(d);
  let tope = 400;
  while (esNoLaborable(x, horario) && tope > 0) { x = sumarDias(x, 1); tope -= 1; }
  return x;
}

/** Cuanto detalle entra en la cabecera con este zoom. No lo elige una persona: lo elige el espacio. */
export function detalleDelEje(pxPorDia) {
  if (pxPorDia >= 22) return 'dia';
  if (pxPorDia >= 7) return 'semana';
  return 'mes';
}

/**
 * El eje completo para un rango y un zoom.
 *
 * Devuelve las columnas ya calculadas —no funciones que las calculen despues— porque las tres
 * vistas las recorren en el mismo render y recalcularlas por vista es la forma mas facil de que
 * una quede corrida.
 */
export function construirEje({ desde, hasta, pxPorDia, horario }) {
  const inicio = aFecha(desde) || new Date();
  const fin = aFecha(hasta) || sumarDias(inicio, 30);
  const total = Math.max(1, diasEntre(inicio, fin) + 1);
  const px = Math.max(PX_POR_DIA_MIN, Math.min(PX_POR_DIA_MAX, pxPorDia || PX_POR_DIA_DEFECTO));
  const detalle = detalleDelEje(px);

  const dias = [];
  const findes = [];
  const meses = [];
  const marcas = [];
  let mesActual = -1;
  let mesDesde = 0;

  for (let i = 0; i < total; i += 1) {
    const d = sumarDias(inicio, i);
    const finde = esNoLaborable(d, horario);
    dias.push({ i, d, finde, x: i * px, num: d.getDate(), dow: DOW[d.getDay()] });
    if (finde) findes.push({ key: `w${i}`, x: i * px, w: px });
    if (d.getMonth() !== mesActual) {
      if (mesActual >= 0) meses[meses.length - 1].w = i * px - mesDesde;
      mesActual = d.getMonth();
      mesDesde = i * px;
      meses.push({ key: `m${i}`, x: mesDesde, w: 0, label: `${MES_CORTO[mesActual]} ${String(d.getFullYear()).slice(2)}` });
    }
    // La segunda linea de la cabecera: cada dia, cada lunes, o nada.
    if (detalle === 'dia') {
      marcas.push({ key: `t${i}`, x: i * px, w: px, alto: d.getDate(), bajo: DOW[d.getDay()], finde });
    } else if (detalle === 'semana' && d.getDay() === 1) {
      marcas.push({ key: `t${i}`, x: i * px, w: px * 7, alto: `${d.getDate()}`, bajo: MES_CORTO[d.getMonth()], finde: false });
    }
  }
  if (meses.length) meses[meses.length - 1].w = total * px - mesDesde;

  const xDe = (iso) => {
    const d = aFecha(iso);
    if (!d) return null;
    return diasEntre(inicio, d) * px;
  };

  return {
    inicio, fin, total, px, detalle, horario,
    ancho: total * px,
    dias, findes, meses, marcas,
    xDe,
    /** El borde derecho de un dia: donde termina la columna, no donde empieza. */
    xFinDe: (iso) => {
      const x = xDe(iso);
      return x === null ? null : x + px;
    },
    /** La barra de un rango, recortada al eje. `null` si queda entera afuera. */
    barra: (desdeIso, hastaIso) => {
      const a = xDe(desdeIso);
      const b = xDe(hastaIso ?? desdeIso);
      if (a === null || b === null) return null;
      const izq = Math.max(0, a);
      const der = Math.min(total * px, b + px);
      if (der <= 0 || izq >= total * px) return null;
      return { left: izq, width: Math.max(2, der - izq) };
    },
  };
}

/**
 * El zoom nuevo y el desplazamiento que hay que aplicar para que **el dia bajo el puntero se
 * quede donde esta**.
 *
 * Sin esto el zoom es inutilizable: cada rueda te manda a otra parte del calendario y hay que
 * volver a buscar lo que estabas mirando. Es la diferencia entre acercarse a algo y saltar.
 */
export function zoomAnclado({ pxPorDia, factor, xPuntero, scrollLeft }) {
  const nuevo = Math.max(
    PX_POR_DIA_MIN,
    Math.min(PX_POR_DIA_MAX, Math.round(pxPorDia * factor * 100) / 100),
  );
  if (nuevo === pxPorDia) return { pxPorDia, scrollLeft };
  const diaBajoElPuntero = (scrollLeft + xPuntero) / pxPorDia;
  return { pxPorDia: nuevo, scrollLeft: Math.max(0, diaBajoElPuntero * nuevo - xPuntero) };
}
